import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Info,
  Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import { draftEntryService } from '../utils/services/draftEntryService';
import type {
  DraftProductionEntry,
  DraftFieldState,
  FieldSyncStatus,
  EntrySubmissionStatus
} from '../types/draftEntry';
import { cn } from './ui/utils';
import { authService, type AuthUser } from '../utils/auth/authService';
import { getCompanyId } from '../utils/tenantContext';
import { speechRecognition } from '../utils/voice/speechRecognition';
import { enhancedVoiceToForm } from '../utils/ai/EnhancedVoiceToForm';
import {
  uploadProductionPhoto,
  normalizeProductionDate,
} from '../utils/firebase/storage';
import { upsertWellEntry, fetchWellEntryByDate, fetchWellEntries, getHaulTotals } from '../utils/firebase/api';
import { getPreviousIsoDate } from '../utils/dateUtils';

// Production Calculations
import {
  calculateEntryTotals,
  validateEntryBeforeSave,
  getBblPerInch,
  type PreviousEntryData
} from '../utils/services/productionCalculatorService';

// Validation
import {
  validateProductionEntry
} from '../utils/validation/productionValidation';

// Audit Trail
import { auditTrailService } from '../utils/audit/auditTrailService';

// Sub-components
import { WellSelector } from './data-entry/WellSelector';
import { ProductionInputs } from './data-entry/ProductionInputs';
import { TankLevels, TankReading, KolorKutReading } from './data-entry/TankLevels';
import { MediaUpload } from './data-entry/MediaUpload';
import { ActionButtons } from './data-entry/ActionButtons';
import { SubmissionStatus } from './data-entry/FieldSyncIndicator';
// Manager approval workflow removed - pumpers can now edit any entry directly
// import { ApprovalStatusBanner, EditRequestForm } from './data-entry/ManagerApprovalWorkflow';

// ==================== LOCAL TYPE DEFINITIONS ====================

/**
 * Well tank configuration as stored on well document
 * Handles the multiple formats that exist in the database
 */
interface WellTankConfig {
  id?: string;
  tankId?: string;
  tankNumber?: number;
  capacity?: number;
  bblPerInch?: number;
  heightInches?: number;
  name?: string;
  type?: 'oil' | 'water';
}

/**
 * Stored gauge data from production entry
 */
interface StoredGaugeData {
  tankId?: string;
  tankNumber?: number;
  topGauge?: number | string;
  bottomGauge?: number | string;
  barrels?: number;
  netVolume?: number;
  kolorKut?: {
    enabled?: boolean;
    waterCutInches?: number | string;
    notes?: string;
  };
}

interface DataEntryProps {
  well?: {
    id: string;
    name?: string;
    location?: string;
    latitude?: number;
    longitude?: number;
    oilTanks?: WellTankConfig[];
    waterTanks?: WellTankConfig[];
    tankConfiguration?: {
      oilTanks?: WellTankConfig[];
      waterTanks?: WellTankConfig[];
    };
    [key: string]: unknown; // Allow other properties
  };
  wells?: Array<{
    id: string;
    name?: string;
    tankConfiguration?: {
      oilTanks?: WellTankConfig[];
      waterTanks?: WellTankConfig[];
    };
    oilTanks?: WellTankConfig[];
    waterTanks?: WellTankConfig[];
    [key: string]: unknown;
  }>;
  onClose: () => void;
  onSave: () => void;
  isDarkMode?: boolean;
}

interface ProductionPhoto {
  id: string;
  url: string;
  storagePath: string;
  filename: string;
  label: string;
  uploadedAt: string;
}

interface VoiceMemoItem {
  id: string;
  url: string;
  storagePath: string;
  filename: string;
  duration: number;
  uploadedAt: string;
}

// Voice memo limit constant - reserved for future voice memo upload feature
const _MAX_VOICE_MEMOS = 5;

// Utility to extract filename from storage path - reserved for media display feature
const _extractFilename = (path: string): string => {
  if (!path) return '';
  const segments = path.split('/');
  return segments[segments.length - 1] || path;
};

type EquipmentKey =
  | 'pumpJack'
  | 'motor'
  | 'flowLines'
  | 'tanks'
  | 'gauges'
  | 'safetyEquipment';

// const equipmentLabels: Record<EquipmentKey, string> = {
//   pumpJack: 'Pump Jack',
//   motor: 'Motor',
//   flowLines: 'Flow Lines',
//   tanks: 'Tanks',
//   gauges: 'Gauges',
//   safetyEquipment: 'Safety Equipment',
// };

const toRadians = (value: number) => (value * Math.PI) / 180;

const calculateDistanceKm = (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number => {
  const earthRadiusKm = 6371;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const haversine =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  return earthRadiusKm * c;
};

const getWellCoordinates = (well: DataEntryProps['well']): { lat: number; lng: number } | null => {
  if (!well) return null;

  const lat =
    well.latitude ??
    (well as { lat?: number }).lat ??
    (well as { location?: { lat?: number; latitude?: number } }).location?.lat ??
    (well as { location?: { lat?: number; latitude?: number } }).location?.latitude ??
    null;
  const lng =
    well.longitude ??
    (well as { lng?: number }).lng ??
    (well as { location?: { lng?: number; longitude?: number } }).location?.lng ??
    (well as { location?: { lng?: number; longitude?: number } }).location?.longitude ??
    null;

  if (typeof lat === 'number' && typeof lng === 'number') {
    return { lat, lng };
  }
  return null;
};

export default function DataEntry({ well, wells = [], onClose, onSave, isDarkMode = false }: DataEntryProps) {
  const sessionUser = useMemo<AuthUser | null>(() => authService.getCurrentUser(), []);
  const availableWells = useMemo(() => {
    if (wells.length) return wells;
    return well ? [well] : [];
  }, [well, wells]);

  const [selectedWellId, setSelectedWellId] = useState<string | null>(() => {
    if (well?.id) return well.id;
    if (wells.length) return wells[0].id;
    return null;
  });

  useEffect(() => {
    if (!availableWells.length) {
      setSelectedWellId(null);
      return;
    }
    setSelectedWellId((prev) => {
      if (prev && availableWells.some((item) => item.id === prev)) {
        return prev;
      }
      return availableWells[0].id;
    });
  }, [availableWells]);

  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locationStatus, setLocationStatus] = useState<'pending' | 'granted' | 'denied'>('pending');
  const didAutoSelectRef = useRef(false);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setLocationStatus('denied');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationStatus('granted');
      },
      () => {
        setLocationStatus('denied');
      },
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: 10_000 }
    );
  }, []);

  const orderedWells = useMemo(() => {
    const enriched = availableWells.map((entry) => {
      const coords = getWellCoordinates(entry);
      const distanceKm = userCoords && coords ? calculateDistanceKm(userCoords, coords) : null;
      return { well: entry, distanceKm };
    });

    enriched.sort((a, b) => {
      if (a.distanceKm == null && b.distanceKm == null) {
        return (a.well?.name || '').localeCompare(b.well?.name || '');
      }
      if (a.distanceKm == null) return 1;
      if (b.distanceKm == null) return -1;
      return a.distanceKm - b.distanceKm;
    });

    return enriched;
  }, [availableWells, userCoords]);

  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!orderedWells.length) return;
    if (!orderedWells[0].well?.id) return;
    if (!userCoords) return;

    setSelectedWellId(orderedWells[0].well.id);
    didAutoSelectRef.current = true;
  }, [orderedWells, userCoords]);

  useEffect(() => {
    if (!orderedWells.length) return;
    if (!selectedWellId) {
      setSelectedWellId(orderedWells[0].well.id);
      return;
    }
    if (!orderedWells.some((entry) => entry.well.id === selectedWellId)) {
      setSelectedWellId(orderedWells[0].well.id);
    }
  }, [orderedWells, selectedWellId]);

  const activeWellEntry = useMemo(() => {
    if (!orderedWells.length) return null;
    if (!selectedWellId) return orderedWells[0];
    return orderedWells.find((entry) => entry.well.id === selectedWellId) ?? orderedWells[0];
  }, [orderedWells, selectedWellId]);

  const activeWell = activeWellEntry?.well ?? null;
  // Total wells count - reserved for progress indicator feature
  const _totalWells = orderedWells.length;


  const formatDistance = (distanceKm: number | null) => {
    if (distanceKm == null) {
      if (locationStatus === 'pending') return 'Locating...';
      if (locationStatus === 'denied') return 'Select well';
      return 'Distance unavailable';
    }
    const miles = distanceKm * 0.621371;
    if (miles < 0.1) {
      return `${Math.round(miles * 5280)} ft away`;
    }
    return `${miles.toFixed(1)} mi away`;
  };

  const handleWellChange = useCallback((nextId: string) => {
    if (!nextId) return;
    setSelectedWellId(nextId);
  }, []);

  const activeDistanceLabel = formatDistance(activeWellEntry?.distanceKm ?? null);

  // Date/time for entry - setDate reserved for date picker feature
  const [date, _setDate] = useState(() =>
    new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  );
  // Time tracking - reserved for time-of-day filtering
  const [_time, _setTime] = useState(() =>
    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );

  // Gas Section Fields (new schema)
  const [yesterdaysSales, setYesterdaysSales] = useState('');
  const [gasFlowRate, setGasFlowRate] = useState('');
  const [staticPressure, setStaticPressure] = useState('');
  const [differentialPressure, setDifferentialPressure] = useState('');
  const [totalAccumulated, setTotalAccumulated] = useState('');

  // Oil Section Fields (keeping these)
  const [waterGauge, setWaterGauge] = useState('');
  const [oilGauge, setOilGauge] = useState('');
  const [totalSales, setTotalSales] = useState('');
  const [notes, setNotes] = useState('');
  const [maintenanceCost, setMaintenanceCost] = useState('');

  // Legacy fields (kept for backward compatibility during transition)
  const [gasVolume, setGasVolume] = useState('');
  const [casingPressure, setCasingPressure] = useState('');
  const [tubingPressure, setTubingPressure] = useState('');

  const [tankReadings, setTankReadings] = useState<TankReading[]>([]);

  // Equipment checklist state - used by voice commands to set pump status
  const [_equipment, setEquipment] = useState<Record<EquipmentKey, boolean>>({
    pumpJack: false,
    motor: false,
    flowLines: false,
    tanks: false,
    gauges: false,
    safetyEquipment: false,
  });

  // Draft auto-save state
  const [draft, setDraft] = useState<DraftProductionEntry | null>(null);
  const [fieldStates, setFieldStates] = useState<Map<string, DraftFieldState>>(new Map());
  // Draft loading indicator - reserved for skeleton loading UI
  const [_isLoadingDraft, setIsLoadingDraft] = useState(true);
  const [existingEntryId, setExistingEntryId] = useState<string | null>(null);
  const [isLoadingExistingEntry, setIsLoadingExistingEntry] = useState(false);
  const [originalEntryData, setOriginalEntryData] = useState<Record<string, unknown> | null>(null); // For audit trail comparison
  // Offline state tracking - reserved for offline indicator UI
  const [_isOffline, _setIsOffline] = useState(!navigator.onLine);
  const saveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const DEBOUNCE_DELAY = 300; // ms

  // Enhanced sync status tracking
  const [fieldSyncStatuses, setFieldSyncStatuses] = useState<Map<string, FieldSyncStatus>>(new Map());
  const [submissionStatus, setSubmissionStatus] = useState<EntrySubmissionStatus>('draft');
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  // Manager approval workflow removed - pumpers can edit any entry
  const isEntryLocked = false; // Always allow editing

  // Validation and error handling - enhanced with real-time validation
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());
  const [validationWarnings, setValidationWarnings] = useState<Map<string, string>>(new Map());
  // Failed saves tracking - reserved for retry UI feature
  const [_failedSaves, _setFailedSaves] = useState<Set<string>>(new Set());
  // Historical entries for anomaly detection - shape from Firestore production_entries
  const [previousEntries, setPreviousEntries] = useState<Record<string, unknown>[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoadingPreviousEntries, setIsLoadingPreviousEntries] = useState(false);
  const [isFirstEntry, setIsFirstEntry] = useState(false); // Track if no previous entries exist
  const unsavedChangesRef = useRef(false);

  const [photos, setPhotos] = useState<ProductionPhoto[]>([]);
  // Voice memos - reserved for voice attachment feature
  const [voiceMemos, _setVoiceMemos] = useState<VoiceMemoItem[]>([]);
  // Audio playback tracking - reserved for voice memo playback feature
  const [_playingMemoId, _setPlayingMemoId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Completed entry count - reserved for multi-well progress tracking
  const [_completedCount, _setCompletedCount] = useState(0);

  // Voice-to-form state
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceInterimTranscript, setVoiceInterimTranscript] = useState('');

  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  // Media refs - reserved for voice recording feature
  const _mediaStreamRef = useRef<MediaStream | null>(null);
  const _audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  const parseNumber = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const validateField = useCallback((fieldName: string, value: string | number | boolean | null | undefined): string | null => {
    if (value === '' || value === null || value === undefined || value === 0 || value === '0') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : NaN);
    if (isNaN(numValue)) return 'Must be a valid number';
    if (numValue < 0) return 'Cannot be negative';
    switch (fieldName) {
      case 'casingPressure': if (numValue > 5000) return 'Exceeds maximum 5000 PSI'; break;
      case 'tubingPressure': if (numValue > 3000) return 'Exceeds maximum 3000 PSI'; break;
      case 'gasVolume': if (numValue > 10000) return 'Unusually high value'; break;
      case 'totalSales': if (numValue > 10000) return 'Unusually high value'; break;
      case 'maintenanceCost': if (numValue > 100000) return 'Unusually high value'; break;
    }
    return null;
  }, []);

  const detectAnomaly = useCallback((fieldName: string, value: number): string | null => {
    if (previousEntries.length === 0 || !value || value === 0) return null;
    const fieldMap: Record<string, string> = {
      'gasVolume': 'gasVolumeMcf',
      'casingPressure': 'casingPressurePsi',
      'tubingPressure': 'tubingPressurePsi',
      'totalSales': 'totalSalesBbl',
    };
    const dbFieldName = fieldMap[fieldName];
    if (!dbFieldName) return null;
    const previousValues = previousEntries
      .map(entry => entry[dbFieldName] as number | undefined | null)
      .filter((v): v is number => v != null && typeof v === 'number' && v > 0);
    if (previousValues.length < 3) return null;
    const avg = previousValues.reduce((sum, v) => sum + v, 0) / previousValues.length;
    const changePercent = Math.abs((value - avg) / avg) * 100;
    if (changePercent > 50) return `⚠️ ${changePercent.toFixed(0)}% different from average`;
    return null;
  }, [previousEntries]);

  useEffect(() => {
    const loadPreviousEntries = async () => {
      if (!activeWell) {
        setPreviousEntries([]);
        setIsFirstEntry(false);
        return;
      }
      setIsLoadingPreviousEntries(true);
      try {
        const companyId = getCompanyId();
        if (!companyId) return;
        const { fetchWellEntries } = await import('../utils/firebase/api');
        const entries = await fetchWellEntries(companyId, activeWell.id, 7);
        setPreviousEntries(entries || []);
        // Check if this is the first entry for this well
        setIsFirstEntry(!entries || entries.length === 0);
      } catch (error) {
        console.error('Failed to load previous entries:', error);
        setIsFirstEntry(true); // Assume first entry if fetch fails
      } finally {
        setIsLoadingPreviousEntries(false);
      }
    };
    loadPreviousEntries();
  }, [activeWell]);

  useEffect(() => {
    const hasChanges = Array.from(fieldStates.values()).some(state =>
      state.isModified || (state.value !== undefined && state.value !== null && state.value !== '' && state.value !== 0)
    );
    unsavedChangesRef.current = hasChanges;
    setHasUnsavedChanges(hasChanges);
  }, [fieldStates, tankReadings, gasVolume, casingPressure, tubingPressure, waterGauge, oilGauge, totalSales, notes, maintenanceCost]);

  // Keyboard shortcuts (Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!isSaving) handleSaveEntry();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSaving]);

  useEffect(() => {
    if (!activeWell) {
      setTankReadings([]);
      return;
    }
    const readings: TankReading[] = [];
    const oilTanks = activeWell.oilTanks || activeWell.tankConfiguration?.oilTanks || [];
    const waterTanks = activeWell.waterTanks || activeWell.tankConfiguration?.waterTanks || [];

    (oilTanks as WellTankConfig[]).forEach((tank, index: number) => {
      // Use configured bblPerInch, or calculate from capacity using standard tank configs
      const tankCapacity = tank.capacity || 500; // Default to 500 BBL tank
      readings.push({
        tankId: tank.id || tank.tankId || `oil-${index + 1}`,
        tankNumber: tank.tankNumber ?? index + 1,
        type: 'oil',
        topGauge: '',
        bottomGauge: '',
        netVolume: 0,
        tankName: `Oil Tank ${index + 1}`,
        bblPerInch: tank.bblPerInch || getBblPerInch(tankCapacity),
        capacity: tankCapacity,
      });
    });
    (waterTanks as WellTankConfig[]).forEach((tank, index: number) => {
      // Use configured bblPerInch, or calculate from capacity using standard tank configs
      const tankCapacity = tank.capacity || 500; // Default to 500 BBL tank
      readings.push({
        tankId: tank.id || tank.tankId || `water-${index + 1}`,
        tankNumber: tank.tankNumber ?? index + 1,
        type: 'water',
        topGauge: '',
        bottomGauge: '',
        netVolume: 0,
        tankName: `Water Tank ${index + 1}`,
        bblPerInch: tank.bblPerInch || getBblPerInch(tankCapacity),
        capacity: tankCapacity,
      });
    });
    setTankReadings(readings);
  }, [activeWell]);

  const handleFieldChange = useCallback(
    (fieldName: string, value: string | number | boolean | null | undefined) => {
      if (!draft) return;

      // Entry locking removed - pumpers can always edit entries
      // Previously: if (isEntryLocked) { toast.error('...'); return; }

      if (saveTimeoutRef.current[fieldName]) clearTimeout(saveTimeoutRef.current[fieldName]);

      const validationError = validateField(fieldName, value);
      const numValue = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : NaN);
      const anomalyWarning = !isNaN(numValue) && numValue > 0 ? detectAnomaly(fieldName, numValue) : null;

      const existingState = fieldStates.get(fieldName);
      const isModified = existingState?.originalValue !== undefined && existingState.originalValue !== value;

      // Update sync status to 'modified' while user is typing
      setFieldSyncStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(fieldName, 'modified');
        return newMap;
      });

      const newState: DraftFieldState = {
        value,
        originalValue: existingState?.originalValue,
        savedAt: new Date().toISOString(),
        isSaved: false,
        hasError: !!validationError,
        errorMessage: validationError || anomalyWarning || undefined,
        isModified,
        syncStatus: 'modified',
      };

      setFieldStates(prev => {
        const newMap = new Map(prev);
        newMap.set(fieldName, { ...newState });
        return newMap;
      });

      if (validationError) {
        setFieldSyncStatuses(prev => {
          const newMap = new Map(prev);
          newMap.set(fieldName, 'error');
          return newMap;
        });
        return;
      }

      saveTimeoutRef.current[fieldName] = setTimeout(async () => {
        try {
          // Update to saving status
          setFieldSyncStatuses(prev => {
            const newMap = new Map(prev);
            newMap.set(fieldName, 'saving');
            return newMap;
          });

          // Save field with status callback
          const finalStatus = await draftEntryService.saveField(
            draft.id,
            fieldName,
            value,
            (status) => {
              setFieldSyncStatuses(prev => {
                const newMap = new Map(prev);
                newMap.set(fieldName, status);
                return newMap;
              });
            }
          );

          const existingState = fieldStates.get(fieldName);
          setFieldStates(prev => {
            const newMap = new Map(prev);
            newMap.set(fieldName, {
              value,
              originalValue: existingState?.originalValue,
              savedAt: new Date().toISOString(),
              isSaved: true,
              hasError: false,
              errorMessage: anomalyWarning || undefined,
              isModified: existingState?.originalValue !== undefined && existingState.originalValue !== value,
              syncStatus: finalStatus,
            });
            return newMap;
          });

          // Update pending sync count
          updatePendingSyncCount();

        } catch (error) {
          console.error('Save failed', error);
          setFieldSyncStatuses(prev => {
            const newMap = new Map(prev);
            newMap.set(fieldName, 'error');
            return newMap;
          });

          // Entry locking removed - show generic error instead
          if (error instanceof Error) {
            toast.error(`Save failed: ${error.message}`);
          }
        }
      }, DEBOUNCE_DELAY);
    },
    [draft, validateField, detectAnomaly, isEntryLocked]
  );

  // Update pending sync count
  const updatePendingSyncCount = useCallback(async () => {
    if (!draft) return;
    const count = await draftEntryService.getPendingSyncCount(draft.id);
    setPendingSyncCount(count);
  }, [draft]);

  // Retry sync for a specific field
  const handleRetrySync = useCallback(async (fieldName: string) => {
    if (!draft) return;

    try {
      setFieldSyncStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(fieldName, 'syncing');
        return newMap;
      });

      // Fire and forget - the callback handles status updates
      await draftEntryService.retryFieldSync(
        draft.id,
        fieldName,
        (newStatus) => {
          setFieldSyncStatuses(prev => {
            const newMap = new Map(prev);
            newMap.set(fieldName, newStatus);
            return newMap;
          });
        }
      );

      updatePendingSyncCount();
      toast.success(`${fieldName} synced successfully`);
    } catch (error) {
      console.error('Retry sync failed:', error);
      setFieldSyncStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(fieldName, 'error');
        return newMap;
      });
      toast.error(`Failed to sync ${fieldName}`);
    }
  }, [draft, updatePendingSyncCount]);

  // Handle tank field retry sync
  const handleTankRetrySync = useCallback((tankId: string, field: string) => {
    handleRetrySync(`tank_${tankId}_${field}`);
  }, [handleRetrySync]);

  // Manager approval workflow removed - this callback is no longer used
  // const handleEditRequestSubmit = useCallback((request: EditApprovalRequest) => {
  //   setCurrentEditRequest(request);
  //   setSubmissionStatus('edit_requested');
  //   setShowEditRequestForm(false);
  // }, []);

  const handleTankGaugeChange = useCallback((tankId: string, field: 'topGauge' | 'bottomGauge', value: string) => {
    setTankReadings(prev => prev.map(reading => {
      if (reading.tankId !== tankId) return reading;
      const updated = { ...reading, [field]: value };
      if (updated.topGauge && updated.bottomGauge) {
        const top = parseFloat(updated.topGauge) || 0;
        const bottom = parseFloat(updated.bottomGauge) || 0;
        const inches = top - bottom;
        if (bottom > top) updated.netVolume = 0;
        else updated.netVolume = Math.max(0, inches * (updated.bblPerInch || 0));
      } else {
        updated.netVolume = 0;
      }
      return updated;
    }));
  }, []);

  // Handle Kolor Kut changes for oil tanks
  const handleKolorKutChange = useCallback((tankId: string, kolorKut: KolorKutReading) => {
    setTankReadings(prev => prev.map(reading => {
      if (reading.tankId !== tankId) return reading;
      return { ...reading, kolorKut };
    }));
  }, []);

  // Restore tank readings (simplified vs original for brevity, but functional intent preserved)
  const hasRestoredTankReadingsRef = useRef(false);
  useEffect(() => {
    if (!draft || !activeWell || hasRestoredTankReadingsRef.current || tankReadings.length === 0) return;
    const savedTankReadings = draft.fields?.['tankReadings']?.value;
    if (!savedTankReadings || !Array.isArray(savedTankReadings)) {
      hasRestoredTankReadingsRef.current = true;
      return;
    }
    const hasExistingValues = tankReadings.some(t => t.topGauge || t.bottomGauge || t.kolorKut?.enabled);
    if (hasExistingValues) {
      hasRestoredTankReadingsRef.current = true;
      return;
    }
    // Merge saved readings with current tank structure
    const merged = tankReadings.map(t => {
      const saved = (savedTankReadings as TankReading[]).find(s => s.tankId === t.tankId);
      if (saved && (saved.topGauge || saved.bottomGauge || saved.kolorKut?.enabled)) {
        return {
          ...t,
          topGauge: saved.topGauge || t.topGauge,
          bottomGauge: saved.bottomGauge || t.bottomGauge,
          netVolume: saved.netVolume || t.netVolume,
          kolorKut: saved.kolorKut || t.kolorKut
        };
      }
      return t;
    });
    setTankReadings(merged);
    hasRestoredTankReadingsRef.current = true;
  }, [draft, activeWell, tankReadings.length]);

  // Persist tank readings (including Kolor Kut data)
  useEffect(() => {
    if (!draft || tankReadings.length === 0) return;
    const hasValues = tankReadings.some(t => t.topGauge || t.bottomGauge || t.kolorKut?.enabled);
    if (!hasValues) return;
    const timeoutId = setTimeout(async () => {
      await draftEntryService.saveField(draft.id, 'tankReadings', tankReadings);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [tankReadings, draft]);

  // Load Entry/Draft Logic (Consolidated)
  useEffect(() => {
    const loadEntryOrDraft = async () => {
      if (!activeWell || !date) {
        setIsLoadingDraft(false);
        setIsLoadingExistingEntry(false);
        return;
      }
      setIsLoadingDraft(true);
      setIsLoadingExistingEntry(true);
      try {
        const companyId = getCompanyId();
        const userId = sessionUser?.id || 'anonymous';
        const entryDate = normalizeProductionDate(date);

        const existingEntry = await fetchWellEntryByDate(companyId, activeWell.id, entryDate);
        if (existingEntry) {
          setExistingEntryId(existingEntry.id);
          const entryIsSubmitted = existingEntry.status === 'submitted' || existingEntry.status === 'approved';
          setIsSubmitted(entryIsSubmitted);

          // Entry locking removed - pumpers can edit any entry
          // Just track submission status for UI display, don't lock
          if (entryIsSubmitted) {
            setSubmissionStatus('confirmed');
            // Previously: setIsEntryLocked(true); - removed to allow edits

            // Set all field sync statuses to 'synced' (editable)
            const syncedStatuses = new Map<string, FieldSyncStatus>();
            ['gasVolume', 'casingPressure', 'tubingPressure', 'totalSales', 'maintenanceCost', 'notes'].forEach(field => {
              syncedStatuses.set(field, 'synced');
            });
            setFieldSyncStatuses(syncedStatuses);
          } else {
            setSubmissionStatus('draft');
          }

          // Store original values for audit trail comparison
          setOriginalEntryData({
            gasVolumeMcf: existingEntry.gasVolumeMcf,
            casingPressurePsi: existingEntry.casingPressurePsi,
            tubingPressurePsi: existingEntry.tubingPressurePsi,
            totalSalesBbl: existingEntry.totalSalesBbl,
            maintenanceCost: existingEntry.maintenanceCost,
            notes: existingEntry.notes,
            tankGauges: existingEntry.tankGauges,
            photos: existingEntry.photos
          });

          // Hydrate local state AND fieldStates for green styling
          const newStates = new Map<string, DraftFieldState>();
          const newSyncStatuses = new Map<string, FieldSyncStatus>();

          const hydrate = (key: string, val: string | number | null | undefined) => {
            if (val === undefined || val === null) return;
            // Set form state
            if (key === 'gasVolume') setGasVolume(String(val));
            if (key === 'casingPressure') setCasingPressure(String(val));
            if (key === 'tubingPressure') setTubingPressure(String(val));
            if (key === 'totalSales') setTotalSales(String(val));
            if (key === 'maintenanceCost') setMaintenanceCost(String(val));
            if (key === 'notes') setNotes(String(val));

            // Set field state for UI (Green check)
            newStates.set(key, {
              value: val,
              originalValue: val,
              savedAt: new Date().toISOString(),
              isSaved: true,
              hasError: false,
              isModified: false,
              syncStatus: entryIsSubmitted ? 'submitted' : 'synced',
            });

            // Set sync status
            newSyncStatuses.set(key, entryIsSubmitted ? 'submitted' : 'synced');
          };

          hydrate('gasVolume', existingEntry.gasVolumeMcf);
          hydrate('casingPressure', existingEntry.casingPressurePsi);
          hydrate('tubingPressure', existingEntry.tubingPressurePsi);
          hydrate('totalSales', existingEntry.totalSalesBbl);
          hydrate('maintenanceCost', existingEntry.maintenanceCost);
          hydrate('notes', existingEntry.notes);

          // Hydrate new Gas Section fields
          if (existingEntry.yesterdaysSalesMcf !== undefined) setYesterdaysSales(String(existingEntry.yesterdaysSalesMcf));
          if (existingEntry.gasFlowRateMcfDay !== undefined) setGasFlowRate(String(existingEntry.gasFlowRateMcfDay));
          if (existingEntry.staticPressurePsi !== undefined) setStaticPressure(String(existingEntry.staticPressurePsi));
          if (existingEntry.differentialPressurePsi !== undefined) setDifferentialPressure(String(existingEntry.differentialPressurePsi));
          if (existingEntry.totalAccumulatedMcf !== undefined) setTotalAccumulated(String(existingEntry.totalAccumulatedMcf));

          // =========================================================================
          // HYDRATE TANK READINGS FROM SAVED ENTRY
          // Convert stored tankGauges format back to local tankReadings state
          // This ensures tank data reloads properly when revisiting an entry
          // =========================================================================
          if (existingEntry.tankGauges && activeWell) {
            const hydratedReadings: TankReading[] = [];
            const oilTanks = activeWell.oilTanks || activeWell.tankConfiguration?.oilTanks || [];
            const waterTanks = activeWell.waterTanks || activeWell.tankConfiguration?.waterTanks || [];

            // Hydrate oil tanks from saved tankGauges.oil array
            const savedOilGauges = (existingEntry.tankGauges.oil || []) as StoredGaugeData[];
            (oilTanks as WellTankConfig[]).forEach((tank, index: number) => {
              const tankId = tank.id || tank.tankId || `oil-${index + 1}`;
              // Find matching saved gauge by tankId or tankNumber
              const savedGauge = savedOilGauges.find(
                g => g.tankId === tankId || g.tankNumber === index + 1
              );

              hydratedReadings.push({
                tankId,
                tankNumber: tank.tankNumber ?? index + 1,
                type: 'oil' as const,
                topGauge: savedGauge?.topGauge?.toString() || '',
                bottomGauge: savedGauge?.bottomGauge?.toString() || '',
                netVolume: savedGauge?.barrels || savedGauge?.netVolume || 0,
                tankName: `Oil Tank ${index + 1}`,
                bblPerInch: tank.bblPerInch || 0,
                capacity: tank.capacity || 0,
                // Restore Kolor-Kut reading if present
                kolorKut: savedGauge?.kolorKut ? {
                  enabled: true, // If kolorKut exists in saved data, it was enabled
                  waterCutInches: savedGauge.kolorKut.waterCutInches?.toString() || '',
                  notes: savedGauge.kolorKut.notes || ''
                } : undefined
              });
            });

            // Hydrate water tanks from saved tankGauges.water array
            const savedWaterGauges = (existingEntry.tankGauges.water || []) as StoredGaugeData[];
            (waterTanks as WellTankConfig[]).forEach((tank, index: number) => {
              const tankId = tank.id || tank.tankId || `water-${index + 1}`;
              // Find matching saved gauge by tankId or tankNumber
              const savedGauge = savedWaterGauges.find(
                g => g.tankId === tankId || g.tankNumber === index + 1
              );

              hydratedReadings.push({
                tankId,
                tankNumber: tank.tankNumber ?? index + 1,
                type: 'water' as const,
                topGauge: savedGauge?.topGauge?.toString() || '',
                bottomGauge: savedGauge?.bottomGauge?.toString() || '',
                netVolume: savedGauge?.barrels || savedGauge?.netVolume || 0,
                tankName: `Water Tank ${index + 1}`,
                bblPerInch: tank.bblPerInch || 0,
                capacity: tank.capacity || 0,
              });
            });

            // Update tankReadings state with hydrated values
            if (hydratedReadings.length > 0) {
              setTankReadings(hydratedReadings);
            }
          }

          setFieldStates(newStates);
          setFieldSyncStatuses(newSyncStatuses);

          setIsLoadingDraft(false);
          setIsLoadingExistingEntry(false);
          return;
        }

        // Load Draft
        setExistingEntryId(null);
        setIsSubmitted(false);
        setSubmissionStatus('draft');
        // Entry locking removed: setIsEntryLocked(false);
        setOriginalEntryData(null); // Clear for new entries - audit will log as 'create'
        const existingDraft = await draftEntryService.loadDraft(
          activeWell.id, activeWell.name || 'Well', userId, sessionUser?.name || 'Unknown', entryDate
        );
        setDraft(existingDraft);

        // Set submission status from draft (no longer locks entries)
        if (existingDraft?.submissionStatus) {
          setSubmissionStatus(existingDraft.submissionStatus);
          // Entry locking removed - previously locked if submitted/confirmed
        }

        if (existingDraft?.fields) {
          const newFieldStates = new Map<string, DraftFieldState>();
          const newSyncStatuses = new Map<string, FieldSyncStatus>();

          Object.entries(existingDraft.fields).forEach(([k, v]) => {
            const fieldState = v as DraftFieldState;
            newFieldStates.set(k, fieldState);
            newSyncStatuses.set(k, fieldState.syncStatus || 'pristine');

            // Original fields
            if (k === 'gasVolume') setGasVolume(String(fieldState.value || ''));
            if (k === 'casingPressure') setCasingPressure(String(fieldState.value || ''));
            if (k === 'tubingPressure') setTubingPressure(String(fieldState.value || ''));
            if (k === 'totalSales') setTotalSales(String(fieldState.value || ''));
            if (k === 'maintenanceCost') setMaintenanceCost(String(fieldState.value || ''));
            if (k === 'notes') setNotes(String(fieldState.value || ''));
            
            // New Gas Section fields
            if (k === 'yesterdaysSales') setYesterdaysSales(String(fieldState.value || ''));
            if (k === 'gasFlowRate') setGasFlowRate(String(fieldState.value || ''));
            if (k === 'staticPressure') setStaticPressure(String(fieldState.value || ''));
            if (k === 'differentialPressure') setDifferentialPressure(String(fieldState.value || ''));
            if (k === 'totalAccumulated') setTotalAccumulated(String(fieldState.value || ''));
          });
          setFieldStates(newFieldStates);
          setFieldSyncStatuses(newSyncStatuses);
        }

        // Update pending sync count
        if (existingDraft) {
          const count = await draftEntryService.getPendingSyncCount(existingDraft.id);
          setPendingSyncCount(count);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingDraft(false);
        setIsLoadingExistingEntry(false);
      }
    };
    loadEntryOrDraft();
  }, [activeWell?.id, date, sessionUser?.id]);

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !files.length) return;
    const companyId = getCompanyId();
    if (!companyId || !activeWell) return;
    setUploadingPhoto(true);
    const normalizedDate = normalizeProductionDate(date);
    const uploads: ProductionPhoto[] = [];
    for (const file of Array.from(files)) {
      try {
        const result = await uploadProductionPhoto(companyId, activeWell.id, normalizedDate, file);
        if (result) {
          uploads.push({
            id: result.storagePath, url: result.downloadUrl, storagePath: result.storagePath,
            filename: result.storagePath.split('/').pop() || file.name, label: file.name, uploadedAt: new Date().toISOString()
          });
        }
      } catch (e) {
        console.error(e);
        toast.error('Upload failed');
      }
    }
    if (uploads.length) {
      const newPhotos = [...photos, ...uploads];
      setPhotos(newPhotos);
      if (draft) await draftEntryService.saveField(draft.id, 'photos', newPhotos);
    }
    setUploadingPhoto(false);
  };

  const handleRemovePhoto = (id: string) => setPhotos(p => p.filter(x => x.id !== id));

  // Voice to form - ENHANCED for all field types
  const handleVoiceRecordClick = async () => {
    if (isVoiceListening) {
      if (speechRecognition?.stop) speechRecognition.stop();
      setIsVoiceListening(false);
      if (voiceTranscript) {
        try {
          const result = await enhancedVoiceToForm.parseAndFill(voiceTranscript, 'production_entry', activeWell?.id);
          if (result.success) {
            let fieldsApplied = 0;

            // Gas Volume
            if (result.fields.gasVolume) {
              const val = String(result.fields.gasVolume.value);
              setGasVolume(val);
              handleFieldChange('gasVolume', parseFloat(val));
              fieldsApplied++;
            }
            // Casing Pressure
            if (result.fields.casingPressure) {
              const val = String(result.fields.casingPressure.value);
              setCasingPressure(val);
              handleFieldChange('casingPressure', parseFloat(val));
              fieldsApplied++;
            }
            // Tubing Pressure
            if (result.fields.tubingPressure) {
              const val = String(result.fields.tubingPressure.value);
              setTubingPressure(val);
              handleFieldChange('tubingPressure', parseFloat(val));
              fieldsApplied++;
            }
            // Oil Volume / Oil Gauge (tank1Level maps to oil)
            if (result.fields.tank1Level || result.fields.oilVolume) {
              const val = String((result.fields.tank1Level || result.fields.oilVolume).value);
              setOilGauge(val);
              handleFieldChange('oilGauge', parseFloat(val));
              // Also try to update first oil tank
              if (tankReadings.length > 0) {
                const oilTankIdx = tankReadings.findIndex(t => t.type === 'oil');
                if (oilTankIdx >= 0) {
                  setTankReadings(prev => prev.map((t, i) =>
                    i === oilTankIdx ? { ...t, topGauge: val, netVolume: parseFloat(val) * (t.bblPerInch || 1) } : t
                  ));
                }
              }
              fieldsApplied++;
            }
            // Water Volume / Water Gauge (tank2Level maps to water)
            if (result.fields.tank2Level || result.fields.waterVolume) {
              const val = String((result.fields.tank2Level || result.fields.waterVolume).value);
              setWaterGauge(val);
              handleFieldChange('waterGauge', parseFloat(val));
              // Also try to update first water tank
              if (tankReadings.length > 0) {
                const waterTankIdx = tankReadings.findIndex(t => t.type === 'water');
                if (waterTankIdx >= 0) {
                  setTankReadings(prev => prev.map((t, i) =>
                    i === waterTankIdx ? { ...t, topGauge: val, netVolume: parseFloat(val) * (t.bblPerInch || 1) } : t
                  ));
                }
              }
              fieldsApplied++;
            }
            // Total Sales
            if (result.fields.totalSales) {
              const val = String(result.fields.totalSales.value);
              setTotalSales(val);
              handleFieldChange('totalSales', parseFloat(val));
              fieldsApplied++;
            }
            // Notes
            if (result.fields.notes) {
              const val = String(result.fields.notes.value);
              setNotes(val);
              handleFieldChange('notes', val);
              fieldsApplied++;
            }
            // Pump Status
            if (result.fields.pumpStatus) {
              const status = result.fields.pumpStatus.value;
              if (status === 'running') {
                setEquipment(prev => ({ ...prev, pumpJack: true }));
              } else if (status === 'down') {
                setEquipment(prev => ({ ...prev, pumpJack: false }));
              }
              fieldsApplied++;
            }
            // Stroke Rate
            if (result.fields.strokeRate) {
              handleFieldChange('strokeRate', parseInt(result.fields.strokeRate.value));
              fieldsApplied++;
            }
            // Line Pressure
            if (result.fields.linePressure) {
              handleFieldChange('linePressure', parseInt(result.fields.linePressure.value));
              fieldsApplied++;
            }

            toast.success(`Voice entry applied`, {
              description: `${fieldsApplied} field${fieldsApplied !== 1 ? 's' : ''} filled from voice`
            });
          } else {
            toast.info('No fields recognized. Try saying: "25 barrels oil, 10 barrels water, tubing 350, casing 200"');
          }
        } catch (e) {
          console.error('Voice to form error:', e);
          toast.error('Failed to process voice input');
        }
      }
    } else {
      if (!speechRecognition.isSupported()) { toast.error('Voice not supported'); return; }
      setIsVoiceListening(true);
      setVoiceTranscript('');
      toast.info('Listening... Say values like "25 barrels oil, tubing 350"');
      try {
        speechRecognition.onResult((transcript: string, isFinal: boolean) => {
          if (isFinal) setVoiceTranscript(p => p ? `${p} ${transcript}` : transcript);
          else setVoiceInterimTranscript(transcript);
        });
        speechRecognition.onEnd = () => {
          setIsVoiceListening(false);
          setVoiceInterimTranscript('');
        };
        await speechRecognition.start(true);
      } catch (e) {
        setIsVoiceListening(false);
        toast.error('Failed to start voice recognition');
      }
    }
  };

  const handleResetFields = () => {
    setGasVolume(''); setCasingPressure(''); setTubingPressure(''); setTotalSales(''); setMaintenanceCost(''); setNotes('');
    setTankReadings(p => p.map(x => ({ ...x, topGauge: '', bottomGauge: '', netVolume: 0 })));
    setPhotos([]); setDraft(null); setFieldStates(new Map()); setExistingEntryId(null);
    setValidationErrors(new Map()); setValidationWarnings(new Map());
  };

  // Validate all fields before submission
  const validateAllFields = (): boolean => {
    const validationData = {
      gasVolume,
      casingPressure,
      tubingPressure,
      totalSales,
      maintenanceCost,
      tankReadings: tankReadings.map(t => ({
        tankId: t.tankId,
        topGauge: t.topGauge,
        bottomGauge: t.bottomGauge,
        kolorKut: t.kolorKut ? { waterCutInches: t.kolorKut.waterCutInches } : undefined
      }))
    };

    const result = validateProductionEntry(validationData);

    // Update error and warning state
    setValidationErrors(new Map(Object.entries(result.errors)));
    setValidationWarnings(new Map(Object.entries(result.warnings)));

    if (!result.isValid) {
      const errorCount = Object.keys(result.errors).length;
      toast.error(`Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before submitting`);
    } else if (Object.keys(result.warnings).length > 0) {
      const warningCount = Object.keys(result.warnings).length;
      toast.warning(`${warningCount} value${warningCount > 1 ? 's look' : ' looks'} unusual. Please verify before submitting.`);
    }

    return result.isValid;
  };

  const handleSaveEntry = async () => {
    if (isSaving) return;
    const companyId = getCompanyId();
    if (!companyId || !activeWell) return;

    // Validate all fields before saving
    if (!validateAllFields()) {
      return; // Stop if validation fails
    }

    setIsSaving(true);
    try {
      if (draft && draft.isComplete) {
        await draftEntryService.finalizeDraft(draft.id);
        toast.success('Entry finalized');
        handleResetFields();
        onSave();
        return;
      }
      // Basic Save
      const online = navigator.onLine;
      const entryDate = normalizeProductionDate(date);

      // =========================================================================
      // PRODUCTION CALCULATIONS - Calculate totals at save time
      // =========================================================================
      // Build tank gauge arrays for calculation
      const oilTankGauges = tankReadings.filter(r => r.type === 'oil').map(r => ({
        tankId: r.tankId,
        tankNumber: r.tankNumber,
        barrels: r.netVolume,
        topGauge: parseFloat(r.topGauge) || 0,
        bottomGauge: parseFloat(r.bottomGauge) || 0,
        kolorKut: r.kolorKut?.enabled ? {
          waterCutInches: parseFloat(r.kolorKut.waterCutInches) || 0,
          notes: r.kolorKut.notes || ''
        } : undefined
      }));

      const waterTankGauges = tankReadings.filter(r => r.type === 'water').map(r => ({
        tankId: r.tankId,
        tankNumber: r.tankNumber,
        barrels: r.netVolume,
        topGauge: parseFloat(r.topGauge) || 0,
        bottomGauge: parseFloat(r.bottomGauge) || 0
      }));

      // Fetch haul totals for this date (if any hauling was recorded)
      let haulSummary = { oilBBL: 0, waterBBL: 0 };
      try {
        if (online) {
          haulSummary = await getHaulTotals(companyId, activeWell.id, entryDate);
          if (haulSummary.oilBBL > 0 || haulSummary.waterBBL > 0) {
          }
        }
      } catch (haulError) {
        // Continue without haul data - calculations will still work
      }

      // =========================================================================
      // FETCH PREVIOUS ENTRY for production calculation
      // 
      // Strategy:
      // 1. Try yesterday's entry first (most common case for daily wells)
      // 2. If no yesterday entry, fetch most recent entry before today
      // 3. Use whichever is found for production differential calculation
      // 
      // This handles wells that aren't checked daily (every other day, weekly, etc.)
      // The daysBetweenEntries will be calculated to compute avg daily production
      // =========================================================================
      let previousEntry: PreviousEntryData | null = null;
      try {
        if (online) {
          // Step 1: Try yesterday's entry first
          const yesterdayDate = getPreviousIsoDate(entryDate);
          const yesterdayDoc = await fetchWellEntryByDate(companyId, activeWell.id, yesterdayDate);

          if (yesterdayDoc?.calculatedProduction) {
            previousEntry = {
              netOilBBL: yesterdayDoc.calculatedProduction.netOilBBL || 0,
              grossWaterBBL: yesterdayDoc.calculatedProduction.grossWaterBBL || 0,
              date: yesterdayDate,
            };
          } else {
            // Step 2: No yesterday entry - fetch most recent entry before today

            const recentEntries = await fetchWellEntries(companyId, activeWell.id, 10);
            // Find most recent entry that's BEFORE today
            const mostRecentBefore = recentEntries?.find((e: { date?: string; entryDate?: string }) => {
              const eDate = e.date || e.entryDate;
              return eDate && eDate < entryDate;
            });

            if (mostRecentBefore?.calculatedProduction) {
              const prevDate = mostRecentBefore.date || mostRecentBefore.entryDate;
              previousEntry = {
                netOilBBL: mostRecentBefore.calculatedProduction.netOilBBL || 0,
                grossWaterBBL: mostRecentBefore.calculatedProduction.grossWaterBBL || 0,
                date: prevDate,
              };
            } else {
            }
          }
        }
      } catch (previousEntryError) {
        // Continue without previous data - production will be 0
      }

      // Calculate production totals (includes Kolor-Kut adjustments and daily production)
      const oilTanksConfig = (activeWell.oilTanks || activeWell.tankConfiguration?.oilTanks || []) as WellTankConfig[];
      const waterTanksConfig = (activeWell.waterTanks || activeWell.tankConfiguration?.waterTanks || []) as WellTankConfig[];

      const calculatedProduction = calculateEntryTotals(
        entryDate,
        oilTankGauges,
        waterTankGauges,
        haulSummary,
        previousEntry,
        {
          oilTanks: oilTanksConfig.map(t => ({ tankId: t.id || t.tankId || '', bblPerInch: t.bblPerInch })),
          waterTanks: waterTanksConfig.map(t => ({ tankId: t.id || t.tankId || '', bblPerInch: t.bblPerInch }))
        }
      );


      // =========================================================================
      // VALIDATION - Check entry before save
      // =========================================================================
      const validation = validateEntryBeforeSave(
        entryDate,
        oilTankGauges,
        waterTankGauges,
        calculatedProduction,
        previousEntry
      );

      // Block on errors
      if (!validation.valid) {
        for (const error of validation.errors) {
          toast.error(error);
        }
        setIsSaving(false);
        return;
      }

      // Warn on warnings but allow continue
      if (validation.warnings.length > 0) {
        for (const warning of validation.warnings) {
          toast.warning(warning, { duration: 5000 });
        }
      }

      // Build entry payload with calculations
      const entryPayload = {
        companyId, wellId: activeWell.id, date: entryDate,
        gasVolumeMcf: parseNumber(gasVolume),
        casingPressurePsi: parseNumber(casingPressure),
        tubingPressurePsi: parseNumber(tubingPressure),
        totalSalesBbl: parseNumber(totalSales),
        maintenanceCost: parseNumber(maintenanceCost),
        // New Gas Section Fields
        yesterdaysSalesMcf: parseNumber(yesterdaysSales),
        gasFlowRateMcfDay: parseNumber(gasFlowRate),
        staticPressurePsi: parseNumber(staticPressure),
        differentialPressurePsi: parseNumber(differentialPressure),
        totalAccumulatedMcf: parseNumber(totalAccumulated),
        entryMethod: 'manual',
        tankGauges: {
          oil: oilTankGauges,
          water: waterTankGauges
        },
        // Include calculated production values
        calculatedProduction,
        photos: photos,
        status: 'submitted' // Official Submit
      };
      await upsertWellEntry(companyId, activeWell.id, entryDate, entryPayload);

      // =========================================================================
      // AUDIT TRAIL - Log the production entry for regulatory compliance
      // =========================================================================
      try {
        const entryId = `${activeWell.id}_${entryDate}`;
        const context = {
          wellId: activeWell.id,
          wellName: activeWell.name || 'Unknown Well',
          entryDate
        };

        const newValues = {
          gasVolumeMcf: entryPayload.gasVolumeMcf,
          casingPressurePsi: entryPayload.casingPressurePsi,
          tubingPressurePsi: entryPayload.tubingPressurePsi,
          totalSalesBbl: entryPayload.totalSalesBbl,
          maintenanceCost: entryPayload.maintenanceCost,
          tankGauges: entryPayload.tankGauges,
          photos: entryPayload.photos?.length || 0,
          status: entryPayload.status
        };

        if (originalEntryData) {
          await auditTrailService.logUpdate(
            'production_entry',
            entryId,
            originalEntryData,
            newValues,
            context
          );
        } else {
          await auditTrailService.logCreate(
            'production_entry',
            entryId,
            newValues,
            context
          );
        }
      } catch (auditError) {
        console.warn('Audit logging failed', auditError);
        // Don't fail the save if audit logging fails
      }

      // Update original entry data for subsequent edits
      setOriginalEntryData({
        gasVolumeMcf: entryPayload.gasVolumeMcf,
        casingPressurePsi: entryPayload.casingPressurePsi,
        tubingPressurePsi: entryPayload.tubingPressurePsi,
        totalSalesBbl: entryPayload.totalSalesBbl,
        maintenanceCost: entryPayload.maintenanceCost,
        tankGauges: entryPayload.tankGauges,
        photos: entryPayload.photos
      });

      // =========================================================================
      // Delete the draft after successful submission
      // This prevents orphaned drafts from accumulating
      // =========================================================================
      if (draft?.id) {
        try {
          await draftEntryService.deleteDraft(draft.id);
          setDraft(null);
        } catch (draftDeleteError) {
          // Don't fail the submission if draft deletion fails
        }
      }

      setIsSubmitted(true);
      setHasUnsavedChanges(false);
      setFieldStates(prev => {
        const updated = new Map(prev);
        updated.forEach((v) => { v.isSaved = true; v.isModified = false; });
        return updated;
      });
      toast.success('Entry officially submitted');
      onSave();
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={cn("fixed inset-0 z-[100] overflow-y-auto", isDarkMode ? "text-white bg-slate-950" : "text-gray-900 bg-gray-50")}
    >
      <div className="mx-auto flex min-h-full max-w-4xl flex-col pb-32">
        {/* Header */}
        <div className={cn("sticky top-0 z-50 px-4 py-4 border-b", isDarkMode ? "border-slate-800 bg-slate-950" : "border-gray-200 bg-white")}>
          <div className="flex items-center justify-between gap-4 max-w-4xl mx-auto">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className={cn("p-2 rounded-xl border transition-colors", isDarkMode ? "border-slate-800 bg-slate-900 hover:bg-slate-800" : "border-gray-200 bg-white hover:bg-gray-50")}
            >
              <X className="w-5 h-5" />
            </motion.button>
            <div className="text-center">
              <h2 className="text-sm font-bold">New Well Entry</h2>
              <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-gray-500")}>{date}</p>
            </div>
            <div className="w-9" /> {/* Spacer */}
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Manager approval workflow removed - pumpers can edit any entry directly */}
          {/* Previously showed ApprovalStatusBanner and EditRequestForm here */}

          {/* Submission Status Indicator */}
          {submissionStatus === 'draft' && (
            <SubmissionStatus
              isSubmitted={isSubmitted}
              isConfirmed={false}
              pendingSync={pendingSyncCount}
              totalFields={fieldStates.size}
            />
          )}

          {/* Previously Submitted Entry Info - show that user is editing a submitted entry */}
          {isSubmitted && existingEntryId && (
            <div className={cn(
              "px-4 py-3 rounded-xl flex items-start gap-3",
              isDarkMode 
                ? "bg-green-900/30 border border-green-700/50 text-green-300" 
                : "bg-green-50 border border-green-200 text-green-700"
            )}>
              <Edit3 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-medium">Updating submitted entry</p>
                <p className="text-xs opacity-80 mt-0.5">
                  This entry was previously submitted. Your changes will update the existing record.
                </p>
              </div>
            </div>
          )}

          {/* Duplicate Entry Warning - Show when editing existing non-submitted entry */}
          {existingEntryId && !isSubmitted && !isLoadingExistingEntry && submissionStatus === 'draft' && (
            <div className={cn(
              "px-4 py-3 rounded-xl flex items-start gap-3",
              isDarkMode 
                ? "bg-amber-900/30 border border-amber-700/50 text-amber-300" 
                : "bg-amber-50 border border-amber-200 text-amber-700"
            )}>
              <Edit3 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-medium">Editing existing entry</p>
                <p className="text-xs opacity-80 mt-0.5">
                  An entry already exists for {date}. Your changes will update the existing record.
                </p>
              </div>
            </div>
          )}

          {/* Well Selector */}
          <WellSelector
            selectedWell={activeWellEntry?.well}
            wells={availableWells}
            onSelectWell={handleWellChange}
            distanceLabel={activeDistanceLabel}
            isDarkMode={isDarkMode}
          />

          {/* Production Inputs */}
          <section>
            <ProductionInputs
              // Gas Section Fields
              yesterdaysSales={yesterdaysSales}
              gasFlowRate={gasFlowRate}
              staticPressure={staticPressure}
              differentialPressure={differentialPressure}
              totalAccumulated={totalAccumulated}
              // Oil Section Fields
              totalSales={totalSales}
              maintenanceCost={maintenanceCost}
              onFieldChange={(f, v) => {
                // Gas Section
                if (f === 'yesterdaysSales') setYesterdaysSales(v);
                if (f === 'gasFlowRate') setGasFlowRate(v);
                if (f === 'staticPressure') setStaticPressure(v);
                if (f === 'differentialPressure') setDifferentialPressure(v);
                if (f === 'totalAccumulated') setTotalAccumulated(v);
                // Oil Section
                if (f === 'totalSales') setTotalSales(v);
                if (f === 'maintenanceCost') setMaintenanceCost(v);
                // Legacy mappings for compatibility
                if (f === 'gasVolume') setGasVolume(v);
                if (f === 'casingPressure') setCasingPressure(v);
                if (f === 'tubingPressure') setTubingPressure(v);
                handleFieldChange(f, v);
              }}
              onRetrySync={handleRetrySync}
              fieldStates={fieldStates}
              fieldSyncStatuses={fieldSyncStatuses}
              isDarkMode={isDarkMode}
              validationErrors={validationErrors}
              validationWarnings={validationWarnings}
              isLocked={false}
            />
          </section>

          {/* First Entry Notification */}
          {isFirstEntry && !isLoadingPreviousEntries && (
            <div className={cn(
              "mb-4 px-4 py-3 rounded-xl flex items-start gap-3",
              isDarkMode 
                ? "bg-blue-900/30 border border-blue-700/50 text-blue-300" 
                : "bg-blue-50 border border-blue-200 text-blue-700"
            )}>
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">First entry for this well</p>
                <p className="text-xs opacity-80 mt-0.5">
                  Daily production comparisons and trend analysis will be available after the next entry.
                </p>
              </div>
            </div>
          )}

          {/* Tank Levels */}
          <section>
            <h3 className={cn("text-sm font-bold uppercase tracking-wider mb-4 px-1 flex items-center gap-2", isDarkMode ? "text-slate-500" : "text-gray-400")}>
              Tank Levels
              {/* Lock icon removed - entries are never locked */}
            </h3>
            <TankLevels
              tankReadings={tankReadings}
              onGaugeChange={handleTankGaugeChange}
              onKolorKutChange={handleKolorKutChange}
              onRetrySync={handleTankRetrySync}
              isDarkMode={isDarkMode}
              validationErrors={validationErrors}
              fieldSyncStatuses={fieldSyncStatuses}
              isLocked={false}
            />
          </section>

          {/* Media Upload */}
          <section>
            <h3 className={cn("text-sm font-bold uppercase tracking-wider mb-4 px-1", isDarkMode ? "text-slate-500" : "text-gray-400")}>Evidence & Media</h3>
            <MediaUpload
              photos={photos}
              voiceMemos={voiceMemos}
              onPhotoUpload={handlePhotoUpload}
              onRemovePhoto={handleRemovePhoto}
              isUploadingPhoto={uploadingPhoto}
              isDarkMode={isDarkMode}
              galleryRef={galleryInputRef as React.RefObject<HTMLInputElement>}
              cameraRef={cameraInputRef as React.RefObject<HTMLInputElement>}
              openCamera={() => cameraInputRef.current?.click()}
              openGallery={() => galleryInputRef.current?.click()}
            />
          </section>
        </div>

        {/* Action Buttons */}
        <ActionButtons
          onSave={handleSaveEntry}
          onCancel={() => {
            // Reminder logic: if not submitted and has data, warn user
            if (!isSubmitted && (hasUnsavedChanges || fieldStates.size > 0)) {
              toast.warning('Entry pending submission', {
                description: 'Don\'t forget to submit your analysis officially!',
                duration: 5000
              });
            }
            onClose();
          }}
          isSaving={isSaving}
          isSubmitted={isSubmitted}
          hasUnsavedChanges={hasUnsavedChanges || !!draft}
          isDarkMode={isDarkMode}
          onVoiceRecord={handleVoiceRecordClick}
          isVoiceListening={isVoiceListening}
          voiceTranscript={voiceTranscript}
          voiceInterimTranscript={voiceInterimTranscript}
        />
      </div>
    </div>
  );
}
